// Two dialog helpers for the people feature.
//
// `useStableClose`: the kit's Dialog re-runs its focus effect whenever `onClose` changes identity,
// and the workspace hands dialogs an inline arrow — so every store change (a search answer writing
// users, a presence update) re-rendered the workspace and yanked focus out of the search field
// onto the first button. A stable callback keeps the reader typing.
//
// `useFocusOnMount`: the Dialog focuses the first input OR button in DOM order, which in the new
// conversation sheet is the Personas/Cerradas switch above the field. The field is where a person
// starts typing, so it takes focus after the Dialog's own effect has run (parents' effects run
// after their children's — hence the frame).
import { useCallback, useEffect, useRef, type RefObject } from 'react';

export function useStableClose(onClose: () => void): () => void {
  const ref = useRef(onClose);
  ref.current = onClose;
  return useCallback(() => ref.current(), []);
}

export function useFocusOnMount(ref: RefObject<HTMLElement | null>, selector: string, key: unknown = null): void {
  useEffect(() => {
    const id = requestAnimationFrame(() => ref.current?.querySelector<HTMLElement>(selector)?.focus());
    return () => cancelAnimationFrame(id);
  }, [ref, selector, key]);
}
